import React, { useState } from "react";
import { MessageCircle, X, CheckCircle2 } from "lucide-react";
import { api } from "../../api";

// Culture audit replaces Chatwoot with this: the question is emailed to the
// campaign's support inbox and the employee gets a copy with a reference.
// It's kept separate from survey responses so it can't de-anonymise anyone,
// which is why the email field is never prefilled from the session.
const MAX_LENGTH = 2000;

const getButtonColour = () => {
  try {
    const branding = JSON.parse(sessionStorage.getItem("branding") || "{}");
    return branding?.data?.appearance?.primary_button_colour || "#88a848";
  } catch (error) {
    return "#88a848";
  }
};

const ContactSupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");

  const colour = getButtonColour();

  const reset = () => {
    setMessage("");
    setError("");
    setReference("");
  };

  const toggle = () => {
    if (isOpen && reference) reset();
    setIsOpen((open) => !open);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (isSending) return;
    setError("");
    setIsSending(true);
    try {
      const response = await api.contactSurveySupport({
        email: email.trim(),
        message: message.trim(),
        // Only used server-side to find the campaign's inbox.
        pipeline_id: sessionStorage.getItem("active-pipeline-id"),
      });
      setReference(response?.data?.reference || "");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "We couldn't send your question. Please check your connection and try again."
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      {isOpen && (
        <div className="w-[calc(100vw-2.5rem)] max-w-sm bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 text-white" style={{ backgroundColor: colour }}>
            <p className="text-base font-semibold">Contact Support</p>
            <p className="text-xs opacity-90">Questions about the survey? HR will reply by email.</p>
          </div>

          {reference ? (
            <div className="p-5 text-center">
              <CheckCircle2 className="mx-auto mb-3" size={40} color={colour} />
              <p className="text-sm font-semibold text-gray-800">Your question has been sent</p>
              <p className="text-sm text-gray-600 mt-2">
                A copy has been emailed to <span className="font-medium">{email}</span>.
              </p>
              <p className="text-sm text-gray-600 mt-3">Reference</p>
              <p className="text-base font-semibold tracking-wide text-gray-900">{reference}</p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 text-sm font-medium underline"
                style={{ color: colour }}
              >
                Ask another question
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-4 space-y-3">
              <div>
                <label htmlFor="support-email" className="block text-xs font-medium text-gray-700 mb-1">
                  Your email address
                </label>
                <input
                  id="support-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1"
                />
              </div>
              <div>
                <label htmlFor="support-message" className="block text-xs font-medium text-gray-700 mb-1">
                  Your question
                </label>
                <textarea
                  id="support-message"
                  required
                  rows={4}
                  maxLength={MAX_LENGTH}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your question here"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1"
                />
              </div>
              <p className="text-[11px] leading-snug text-gray-500">
                Your question is sent separately from your survey responses, which stay anonymous.
              </p>
              {error && <p className="text-xs text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={isSending || !email.trim() || !message.trim()}
                className="w-full text-white text-sm font-medium py-2 rounded-md disabled:opacity-60"
                style={{ backgroundColor: colour }}
              >
                {isSending ? "Sending..." : "Send question"}
              </button>
            </form>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-label={isOpen ? "Close contact support" : "Contact support"}
        className="w-14 h-14 flex items-center justify-center rounded-full text-white shadow-lg hover:scale-105 transition-transform"
        style={{ backgroundColor: colour }}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={26} />}
      </button>
    </div>
  );
};

export default ContactSupportWidget;
